import { SB, searchableComputedProperty, teaHash } from '@play-co/replicant';
import adminProperties from '../../admin/admin.properties';
import type { State } from '../../State';
import { encodePacket } from '../../utils/encoder';
import { demographicsProperties } from '../../demographics/demographics.properties';
import { petProperties } from '../../pet/pet.properties';
import { visitProperties } from '../../visit/visit.properties';
import { profileProperties } from '../../profile/profile.properties';
import { duoProperties } from 'src/replicant/duo/duo.properties';
import { match2Properties } from 'src/replicant/match2/tournament/match2-tournament.properties';
import { leagueProperties } from 'src/replicant/match2/leagues/leagues.properties';
import { getBuruFriends } from 'src/replicant/friends/friends.getters';
import { weeklyLeaderboardProperties } from 'src/replicant/weeklyLeaderboard/weeklyLeaderboard.properties';
import { teamProperties } from 'src/replicant/team/team.properties';
import { buddiesProperties } from 'src/replicant/buddies/buddies.properties';
import { payloadComputedProperty } from '@play-co/replicant';
import { cardsProperties } from 'src/replicant/cards/cards.properties';
import { deprecatedComputedProperties } from 'src/replicant/deprecatedComputedProperties';

export default {
  ...adminProperties,
  ...demographicsProperties,
  ...petProperties,
  ...visitProperties,
  ...profileProperties,
  ...duoProperties,
  ...match2Properties,
  ...leagueProperties,
  ...weeklyLeaderboardProperties,
  ...teamProperties,
  ...buddiesProperties,
  ...cardsProperties,

  ...deprecatedComputedProperties,

  /** IDs of playing friends. */
  friendIds: searchableComputedProperty(SB.array(SB.string()), (state: State) =>
    getBuruFriends(state).sort(),
  ),

  /** Number of playing friends. */
  buruFriendsCount: searchableComputedProperty(
    SB.int(),
    (state: State) => getBuruFriends(state).length,
  ),

  updatedAt: searchableComputedProperty(SB.int(), (state: State) => {
    // Round down to latest passed hour to prevent updating index on each replication, and
    // offset with hashed user ID to prevent hourly indexing spikes:
    const offset = Math.floor(teaHash(state.id) * 1000 * 60 * 60);

    return new Date(state.updatedAt - offset).setMinutes(0, 0, 0);
  }),

  createdAt: searchableComputedProperty(SB.int(), (state: State) => {
    return state.createdAt;
  }),

  offlineUpdateAt: searchableComputedProperty(SB.int(), (state: State) => {
    // Round down to latest passed hour to prevent updating index on each replication, and
    // offset with hashed user ID to prevent hourly indexing spikes:
    const offset = Math.floor(teaHash(state.id) * 1000 * 60 * 60);

    return new Date(state.offlineUpdateAt - offset).setMinutes(0, 0, 0);
  }),

  maskedUID: searchableComputedProperty(SB.string(), (state: State) =>
    encodePacket(state.id),
  ),

  /** Player locale with fallback to 'ja' for players who haven't set one. */
  locale: payloadComputedProperty(
    SB.string(),
    (state: State) => state.settings.locale || 'ja',
  ),

  /** Timestamp of the player's last session start. */
  lastSessionAt: payloadComputedProperty(
    SB.number(),
    (state: State) => state.lastSessionAt,
  ),
};
